// XP Calculation Helper Functions

/**
 * Calculate total XP from xpHistory object
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {number} Total XP earned
 */
export const calculateTotalXP = (xpHistory = {}) => {
  return Object.values(xpHistory).reduce((total, quest) => {
    // Only count completed quests for XP
    if (quest.status === 'completed') {
      return total + (quest.value || 0);
    }
    return total;
  }, 0);
};

/**
 * Get all completed quests from xpHistory
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {Array} Array of completed quest objects with their IDs
 */
export const getCompletedQuests = (xpHistory = {}) => {
  return Object.entries(xpHistory)
    .filter(([_, quest]) => quest.status === 'completed')
    .map(([id, quest]) => ({ id, ...quest }));
};

/**
 * Get all failed quests from xpHistory
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {Array} Array of failed quest objects with their IDs
 */
export const getFailedQuests = (xpHistory = {}) => {
  return Object.entries(xpHistory)
    .filter(([_, quest]) => quest.status === 'failed')
    .map(([id, quest]) => ({ id, ...quest }));
};

/**
 * Get current active quest from user data
 * @param {Object} userData - The user data object from Firestore
 * @returns {Object|null} Active quest object or null if none
 */
export const getActiveQuest = (userData) => {
  // Return the activeQuest field directly, or null if it doesn't exist
  return userData?.activeQuest || null;
};

/**
 * Get current active quest from xpHistory (legacy function for backward compatibility)
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {Object|null} Active quest object with ID or null if none
 */
export const getActiveQuestFromHistory = (xpHistory = {}) => {
  const activeEntry = Object.entries(xpHistory)
    .find(([_, quest]) => quest.status === 'active');
  
  return activeEntry ? { id: activeEntry[0], ...activeEntry[1] } : null;
};

/**
 * Get all quests for quest history display
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {Array} Array of all quest objects (completed and failed) with their IDs
 */
export const getQuestHistory = (xpHistory = {}) => {
  return Object.entries(xpHistory)
    .filter(([_, quest]) => quest.status === 'completed' || quest.status === 'failed')
    .map(([id, quest]) => ({ 
      id, 
      ...quest,
      // Add additional fields for better display
      title: quest.title || `Quest ${id.slice(-6)}`, // Fallback title
      reward: quest.value,
      completedAt: quest.completedAt || Date.now(),
      failedAt: quest.failedAt || (quest.status === 'failed' ? Date.now() : null)
    }))
    .sort((a, b) => {
      // Sort by completion/failure date, most recent first
      const dateA = a.completedAt || a.failedAt || 0;
      const dateB = b.completedAt || b.failedAt || 0;
      return dateB - dateA;
    });
};

/**
 * Calculate quest statistics
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @param {Object} userData - The full user data object from Firestore (for active quest)
 * @returns {Object} Object containing quest statistics
 */
export const calculateQuestStats = (xpHistory = {}, userData) => {
  const completed = getCompletedQuests(xpHistory);
  const failed = getFailedQuests(xpHistory);
  const active = getActiveQuest(userData); // Use new function with userData
  const totalXP = calculateTotalXP(xpHistory);
  
  return {
    questsCompleted: completed.length,
    questsFailed: failed.length,
    hasActiveQuest: !!active,
    totalXP,
    successRate: completed.length + failed.length > 0 
      ? Math.round((completed.length / (completed.length + failed.length)) * 100) 
      : 0,
    averageXPPerQuest: completed.length > 0 
      ? Math.round(totalXP / completed.length) 
      : 0
  };
};

/**
 * Get XP gained in a specific time period
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @param {number} days - Number of days to look back
 * @returns {number} XP gained in the specified period
 */
export const getXPInPeriod = (xpHistory = {}, days = 7) => {
  const cutoffDate = Date.now() - (days * 24 * 60 * 60 * 1000);
  
  return Object.values(xpHistory).reduce((total, quest) => {
    if (quest.status === 'completed' && 
        quest.completedAt && 
        quest.completedAt >= cutoffDate) {
      return total + (quest.value || 0);
    }
    return total;
  }, 0);
};

/**
 * Check if user has completed any quests today
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {boolean} True if user completed at least one quest today
 */
export const hasCompletedQuestToday = (xpHistory = {}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();
  
  return Object.values(xpHistory).some(quest => 
    quest.status === 'completed' && 
    quest.completedAt && 
    quest.completedAt >= todayTimestamp
  );
};

/**
 * Get streak information (consecutive days with completed quests)
 * @param {Object} xpHistory - The xpHistory object from Firestore
 * @returns {Object} Object containing current streak and longest streak
 */
export const getQuestStreak = (xpHistory = {}) => {
  const completedQuests = getCompletedQuests(xpHistory)
    .filter(quest => quest.completedAt)
    .sort((a, b) => b.completedAt - a.completedAt);
  
  if (completedQuests.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }
  
  // Get unique days with completed quests
  const completionDays = [...new Set(
    completedQuests.map(quest => {
      const date = new Date(quest.completedAt);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    })
  )].sort((a, b) => b - a);
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();
  
  for (let i = 0; i < completionDays.length; i++) {
    const expectedDay = todayTimestamp - (i * 24 * 60 * 60 * 1000);
    
    if (completionDays[i] === expectedDay) {
      currentStreak++;
      tempStreak++;
    } else {
      break;
    }
  }
  
  // Calculate longest streak
  tempStreak = 0;
  for (let i = 0; i < completionDays.length; i++) {
    if (i === 0 || completionDays[i] === completionDays[i-1] - (24 * 60 * 60 * 1000)) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 1;
    }
  }
  
  return { currentStreak, longestStreak };
}; 